import Vue from 'vue';
import { DataStub, isNullish } from '@tencent/data-schema-core';
import { isExprObject } from '@tencent/data-schema-core/lib/utils/eval';
import { UICorePlugin } from '../../Plugin';
import { tryEval, vueBailDepCollector, writeClipboard } from "../../../lib/utils";
import { cloneDeep, isEqual } from "lodash-es";
import { makeEvalFn } from "../../../lib/utils/stringInterop";
import { newFunction } from "../../../lib/utils/script";
import { getCompiledCatchErrorLogger } from "../../../lib/utils/script/evalRuntime";
const defaultStateSchema = {
  type: 'object',
  fields: []
};
function copyDataPath(ctx, ev) {
  const text = ctx.dataPath;
  writeClipboard(text);
  ctx.uc.$message.success(`已经复制：${text}`);
  ev.stopPropagation();
  ev.preventDefault();
}
function invalidGetter(id) {
  console.error(`[UICore] 不能取变量 ${id} 的值，因为它没有配置 Getter 或者 Getter 存在语法错误`);
}
function invalidSetter(id) {
  throw new Error(`[UICore] 不能赋值到变量 ${id}，因为它没有配置 Setter 或者 Setter 存在语法错误`);
}
class LocalStorageState {
  clearWatch() {
    this.unWatchLsState.forEach(cb => cb());
    this.unWatchLsState = [];
  }
  constructor(uc, renderer) {
    this.unWatchLsState = [];
    this.cacheKey = uc.config.userCacheKey;
    this.pageletId = renderer.ucRootRenderer.ucPagelet.id || '';
    const getSlotKey = () => {
      var _renderer$ucScopedSlo;
      if (renderer.ucIsRoot) return '';
      return `${(_renderer$ucScopedSlo = renderer.ucScopedSlotOwner) === null || _renderer$ucScopedSlo === void 0 || (_renderer$ucScopedSlo = _renderer$ucScopedSlo.comp) === null || _renderer$ucScopedSlo === void 0 ? void 0 : _renderer$ucScopedSlo.componentId}_${renderer.ucScopedSlotId}`;
    };
    this.slotKey = getSlotKey();
  }
  getAll() {
    return JSON.parse(localStorage.getItem(this.cacheKey) || '{}');
  }
  getKey(stateId) {
    return `${this.pageletId}_${this.slotKey}_${stateId}`;
  }
  get(stateId) {
    if (stateId === undefined) return undefined;
    return this.getAll()[this.getKey(stateId)];
  }
  set(stateId, value) {
    const instance = this.getAll();
    instance[this.getKey(stateId)] = value;
    localStorage.setItem(this.cacheKey, JSON.stringify(instance));
  }
}
export class DataSourcePlugin extends UICorePlugin {
  install(uc) {
    const h = this.$createElement;
    if (!uc.config.statePlugin) {
      return;
    }
    uc.hooks.pageletRendererCreated.tap('DataSourcePlugin', renderer => {
      let finishDataSoureInit = false;
      let lastStateSchema;
      const lsState = new LocalStorageState(uc, renderer);
      const disposers = [];
      const dispose = () => disposers.splice(0).forEach(fn => fn());
      renderer.$once('hook:beforeDestroy', dispose);
      renderer.$watch(() => {
        var _renderer$ucPagelet;
        return ((_renderer$ucPagelet = renderer.ucPagelet) === null || _renderer$ucPagelet === void 0 ? void 0 : _renderer$ucPagelet.stateSchema) || defaultStateSchema;
      }, schema => {
        var _$stub$definedFields;
        if (isEqual(lastStateSchema, schema)) return;
        lastStateSchema = cloneDeep(schema);
        const fs = id => lastStateSchema.fields.find(x => x.id === id);
        // 补全缺失的默认值
        const $stub = new DataStub({
          schema,
          value: renderer.state
        });
        const state = {};
        const computedCacheVersions = Vue.observable({});
        const tempCtxForEval = {
          state
        };
        const deferredInitializer = []; // 动态初始值要放到静态的之后来初始化
        const lsWatchStateInit = [];
        dispose();
        disposers.push(() => lsState.clearWatch());
        (_$stub$definedFields = $stub.definedFields) === null || _$stub$definedFields === void 0 || _$stub$definedFields.forEach(field => {
          var _field$ucStateType;
          const id = field.id;
          const type = (_field$ucStateType = field['uc:stateType']) !== null && _field$ucStateType !== void 0 ? _field$ucStateType : 'normal';
          const syncLs = field['uc:syncLocalStorage'];
          if (syncLs && type === 'normal' && !uc.isDesignMode) {
            const watchLsStateChange = () => {
              const unWatch = renderer.$watch(() => renderer.state[id], newValue => {
                lsState.set(id, newValue);
              }, {
                immediate: true,
                deep: true
              });
              lsState.unWatchLsState.push(unWatch);
            };
            lsWatchStateInit.push(watchLsStateChange);
          }
          if (type === 'computed') {
            var _field$ucStateSetter;
            fs(id).description = `**注意** 这是一个 computed 变量，${(_field$ucStateSetter = field['uc:stateSetter']) !== null && _field$ucStateSetter !== void 0 && _field$ucStateSetter.trim() ? '写入时会执行 setter 方法' : '只能读，不可写！'}`;
          }
          // 设计模式需要使用示例值
          if (uc.isDesignMode && !isNullish(field.example)) {
            state[id] = cloneDeep(field.example);
            return;
          }
          // 普通变量
          switch (type) {
            case 'normal':
              {
                if (syncLs && !uc.isDesignMode) {
                  // 优先使用ls覆盖默认值
                  const lsValue = lsState.get(id);
                  if (lsValue !== undefined) {
                    return state[id] = cloneDeep(lsValue);
                  }
                }
                if (isExprObject(field.default)) {
                  const expr = field.default.$expr;
                  deferredInitializer.push(() => {
                    state[id] = tryEval(expr, renderer, tempCtxForEval);
                  });
                } else {
                  state[id] = cloneDeep(field.default);
                }
                return;
              }
            case 'computed':
              {
                var _field$ucStateGetter, _field$ucStateSetter2;
                const getter = (_field$ucStateGetter = field['uc:stateGetter']) === null || _field$ucStateGetter === void 0 ? void 0 : _field$ucStateGetter.trim();
                const setter = (_field$ucStateSetter2 = field['uc:stateSetter']) === null || _field$ucStateSetter2 === void 0 ? void 0 : _field$ucStateSetter2.trim();
                const descriptor = {
                  enumerable: true,
                  configurable: true,
                  get: invalidGetter.bind(null, String(id)),
                  set: invalidSetter.bind(null, String(id))
                };
                // GETTER
                if (getter) {
                  try {
                    const getterFn = makeEvalFn(getter, 1, true);
                    const getterUseCache = !!field['uc:stateGetterUseCache'];
                    if (getterUseCache) {
                      // 使用 $watch 和依赖收集，仅在依赖内容发生变化的时候重新计算值
                      let inited = false;
                      let lastValue = null;
                      Vue.set(computedCacheVersions, id, 0);
                      descriptor.get = () => {
                        // state初始化完成之前不要watch
                        if (!finishDataSoureInit) {
                          return vueBailDepCollector(() => getterFn(renderer));
                        }
                        if (!inited) {
                          inited = true;
                          disposers.push(renderer.$watch(() => getterFn(renderer), value => {
                            computedCacheVersions[id] += 1;
                            lastValue = value;
                          }, {
                            immediate: true
                          }));
                        }
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        computedCacheVersions[id]; // vue dep
                        return lastValue;
                      };
                    } else {
                      descriptor.get = () => getterFn(renderer);
                    }
                  } catch (err) {
                    console.error(`[UICore] 页面变量 ${id} 的 Getter 存在语法错误，无法编译`);
                  }
                }
                // SETTER
                if (setter) {
                  try {
                    descriptor.set = newFunction(['renderer', 'value'], ['with(renderer){try{', setter, '}catch(_err){', getCompiledCatchErrorLogger('_err', setter, `页面变量${id}的Setter`), 'throw _err;}}'].join('\n')).bind(renderer, renderer);
                  } catch (err) {
                    console.error(`[UICore] 页面变量 ${id} 的 Setter 存在语法错误，无法编译`);
                  }
                }
                Object.defineProperty(state, id, descriptor);
                return;
              }
            default:
              {
                state[id] = undefined;
              }
          }
        });
        deferredInitializer.forEach(f => f());
        // 然后更新 declaration
        renderer.addDataSource({
          id: 'state',
          instance: Vue.observable(state),
          schema: {
            ...lastStateSchema,
            id: 'state',
            title: renderer.ucDepth ? '局部区域变量' : '页面变量',
            description: renderer.ucDepth ? '注意：这里 **并不是** 页面变量！如果要访问那里的数据，可通过 `parentScope.xxx` 访问。' : '可在左侧数据源面板里添加/删除',
            'uc:bindable': false
          }
        });
        // 挂载好state后监听需要同步ls的变量
        lsWatchStateInit.forEach(cb => cb());
        finishDataSoureInit = true;
      }, {
        immediate: true,
        deep: !!uc.isDesignMode
      });
    });
    uc.hooks.getActionsOfDataSource.tap('DataSourcePlugin', (list, req) => {
      var _req$dataStub;
      const ans = [...list];
      // --------------------
      if (req.dataPath !== 'state' && req.dataPath !== 'data' && ((_req$dataStub = req.dataStub) === null || _req$dataStub === void 0 || (_req$dataStub = _req$dataStub.schema) === null || _req$dataStub === void 0 ? void 0 : _req$dataStub['uc:allowCopyPath']) !== false) {
        // 除了根节点那两个东西，其他都是可以 copy 路径的, 还有每个数据源的根节点,上面要求的
        ans.push({
          key: 'copyPath',
          icon() {
            const h = this.$createElement;
            return h("a-icon", {
              "attrs": {
                "type": 'copy'
              }
            });
          },
          title: `复制路径 ${req.dataPath}`,
          execute: copyDataPath.bind(null, req)
        });
      }
      // --------------------
      const {
        pathParts
      } = req.dataStub;
      if (pathParts[0] === 'state') {
        // 直接在"state"根上
        if (req.dataPath === 'state') {
          ans.unshift({
            key: 'addState',
            icon() {
              const h = this.$createElement;
              return h("a-icon", {
                "attrs": {
                  "type": 'plus'
                }
              });
            },
            title: '新建变量',
            execute: ev => {
              req.designer.openCreateStateVariableDialog({
                id: 'foobar',
                place: ev.target
              });
            }
          });
        }
        // 在用户临时字段上
        if (pathParts.length === 2) {
          ans.unshift({
            key: 'editState',
            // icon: 'fa fa-edit',
            icon() {
              const h = this.$createElement;
              return h("a-icon", {
                "attrs": {
                  "type": 'edit'
                }
              });
            },
            title: '编辑这个变量',
            execute: ev => {
              const id = pathParts[1];
              req.designer.openEditStateVariableDialog({
                id,
                place: ev.target
              });
            }
          });
        }
      }
      return ans;
    });
  }
}
//# sourceMappingURL=dataSource.js.map
